/**
 * Date Time Formatter Utility Functions
 * 
 * Collection of utility functions for formatting and displaying time values
 * throughout the MacSafety web application. These functions ensure consistent
 * time display across all forms and components that handle time fields.
 * 
 * Key Functions:
 * - formatTimeForDisplay: Converts 24-hour format to 12-hour format with AM/PM
 * 
 * Primary Use Cases:
 * - Converting 24-hour time values to user-friendly 12-hour format
 * - Ensuring consistent time display in form submissions
 * - Supporting forms with time input fields (e.g., incident reports, work permits)
 * - Providing readable time format for user interfaces
 * 
 * Time Format Conversion:
 * - Input: 24-hour format (e.g., "13:51", "00:00", "23:45")
 * - Output: 12-hour format with AM/PM (e.g., "1:51 PM", "12:00 AM", "11:45 PM")
 * 
 * Components and Forms that utilize these functions:
 * - All forms with time input fields for consistent display

 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

/**
 * Formats time from 24-hour format to 12-hour format with AM/PM indicator
 * Converts military time format to user-friendly display format
 * Handles edge cases like midnight (00:00) and noon (12:00) correctly
 * 
 * @param timeValue - Time in HH:mm format (e.g., "13:51", "00:00", "23:45")
 * @returns Formatted time string in 12-hour format with AM/PM (e.g., "1:51 PM", "12:00 AM", "11:45 PM")
 */
export function formatTimeForDisplay(timeValue: string): string {
  // Validate input - return original value if invalid or missing
  if (!timeValue || !timeValue.includes(':')) {
    return timeValue || '';
  }

  // Split time string and convert to numbers
  const [hours, minutes] = timeValue.split(':').map(Number);
  
  // Validate parsed numbers - return original value if parsing failed
  if (isNaN(hours) || isNaN(minutes)) {
    return timeValue;
  }

  let displayHours = hours;
  let period = 'AM';

  // Convert to 12-hour format
  if (hours >= 12) {
    period = 'PM';
    if (hours > 12) {
      displayHours = hours - 12;
    }
  }

  // Handle midnight (00:00) - display as 12:00 AM
  if (hours === 0) {
    displayHours = 12;
  }

  // Format minutes with leading zero if needed for consistent display
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${displayHours}:${formattedMinutes} ${period}`;
} 

/**
 * Utility function to robustly parse a date value from user input or API.
 *
 * - If a user selects a date in the UI (usually a YYYY-MM-DD string),
 *   this ensures we get a correct local Date object for that calendar day.
 * - If a date comes from the API (often an ISO string with time),
 *   this will use the Date constructor so the value is not shifted or misinterpreted.
 *
 * This function prevents timezone issues when converting between UI and API representations.
 * Accepts a string, Date, or null/undefined and returns a Date object or null.
 */
export function parseLocalDate(value: any): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    // If time is included (T or space after date), use Date constructor (UTC/ISO)
    if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(value)) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    // If it's exactly YYYY-MM-DD, treat as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return d;
    }
    return null;
  }
  return null;
}